// @givmo/board — invite a board member to join with their Givmo account (CBG-SEC2, D-CBG-16/17).
//
// ══ WHY THE LINK IS SHOWN TO THE OFFICER, AND WHY THAT IS SAFE NOW ══════════════════════════════
//
// The invitation is emailed to the seat's address when this environment sends mail, and the dialog
// always shows its link with a Copy button: plan §1.7's manual path, where the officer hands it over
// directly — reads it out, pastes it into a message they are already writing.
//
// The link opens nothing by itself. To join, the member signs in with their own Givmo account and
// proves the seat's mailbox: the account's confirmed email matches the seat's address, or they type a
// code mailed to that address ("match or prove", D-CBG-17 decision 1). So whoever holds the link —
// the officer included — can see the invitation page and nothing else; the dialog says so, in words a
// volunteer can act on, because a link that looks ordinary invites ordinary handling.
//
// What is shown and copied is the LINK, the one address the mail also carries (CBG-FIX14,
// `director/director-access-link.ts`), never the bare token.
//
// `email_sent` reports whether a mail also went out; it is FALSE when no template or base URL is
// configured in this environment. Showing that honestly is the point — an officer who assumes an
// email went and it did not has a member who never hears from them. A member who has already joined
// needs no invitation: the backend refuses the mint (`board_seat_already_connected`) and this says so.
//
// A seat for someone who isn't on the board (CBG-YR6-OUTSIDE, frame o4) is invited to its committees:
// the title names them, and the backend's mail takes the committee's words.
//
// ONE COPY OF THE WORDS (CBG-UX9): the title, what the invitation says once it is made and its two
// buttons are exported, because the step after "Add a member" (`invite-step.tsx`) ends in the same
// lines as this dialog, the row's ⋯ menu.

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  type BoardAccessGrantOut,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, testIdProp, toast } from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { breakableLink } from '../../components/breakable-link';
import { useDirectorAccessLink } from '../../director/director-access-link';
import { committeeOnlyWords as co } from '../committees/committee-only-words';

/** The seat an invitation is for: who it names, and the address it goes to. */
export type InvitedSeat = Pick<OrgBoardMemberOut, 'id' | 'display_name' | 'email_of_record'>;

/** An invitation refused with no sentence of the backend's. */
export const INVITATION_FAILED = 'We couldn’t create an invitation. Please try again.';

/** The invitation's title: the board's, or the committees a seat for someone not on it serves on. */
export function invitationTitle(name: string, committees: readonly string[] | null): string {
  return committees !== null && committees.length > 0
    ? co.inviteTitle(name, committees)
    : `Invite ${name} to the board`;
}

/** What the invitation says once it is made: whether it was emailed, its link, and who can use it. */
export function InvitationOutcome({
  seat,
  grant,
  link,
}: {
  seat: InvitedSeat;
  grant: BoardAccessGrantOut;
  link: string;
}) {
  return (
    <>
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {grant.email_sent
          ? `We emailed an invitation to ${seat.email_of_record}. You can also give them this link:`
          : `We didn’t email this invitation, so give ${seat.display_name} this link:`}
      </p>

      <code
        className="block wrap-anywhere rounded-md border border-border-subtle bg-surface-sunken p-12 font-mono text-s12 text-text-strong"
        {...testIdProp('roster-invitation-url')}
      >
        {breakableLink(link)}
      </code>

      <ul className="flex flex-col gap-6 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        <li>
          Only {seat.display_name} can use it: they join with their own Givmo account at{' '}
          {seat.email_of_record}, or with a code we send there.
        </li>
        <li>It works for about a week, and stops once they join or stop serving.</li>
      </ul>
    </>
  );
}

/** The invitation's buttons: Done, and Copy link once there is a link to copy. */
export function InvitationActions({ link, onClose }: { link: string | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copyLink(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Link copied.');
    } catch {
      // Clipboard access can be refused (permissions, an insecure origin). The link is on screen and
      // selectable, so this is a missing convenience rather than a failure worth an error dialog.
      toast.error('We couldn’t copy it — you can select the link and copy it yourself.');
    }
  }

  return (
    <div className="flex justify-end gap-10">
      <Button appearance="saas" tone="secondary" onClick={onClose} testId="roster-invitation-close">
        Done
      </Button>
      {link !== null ? (
        <Button
          appearance="saas"
          tone="primary"
          onClick={() => void copyLink(link)}
          testId="roster-invitation-copy"
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      ) : null}
    </div>
  );
}

export interface InvitationDialogProps {
  orgRef: string;
  seat: OrgBoardMemberOut;
  onClose: () => void;
  /**
   * CBG-YR6-OUTSIDE: a seat for someone who isn't on the board — the committees it is invited to,
   * which the title names. Null or absent: the board's invitation.
   */
  committees?: readonly string[] | null;
}

export function InvitationDialog({
  orgRef,
  seat,
  onClose,
  committees = null,
}: InvitationDialogProps) {
  const heading = invitationTitle(seat.display_name, committees);
  const operations = useBoardOperations();
  const linkFor = useDirectorAccessLink();
  const [grant, setGrant] = useState<BoardAccessGrantOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const requestedRef = useRef(false);

  const mint = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerMintBoardAccessLink({
        path: { org_ref: orgRef, member_id: seat.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => setGrant(data),
    onError: (caught) => {
      if (isGivmoError(caught) && firstErrorCode(caught) === 'board_seat_already_connected') {
        setAlreadyJoined(true);
        return;
      }
      setError(isGivmoError(caught) ? caught.message : INVITATION_FAILED);
    },
  });

  // Mint once when the dialog opens. A ref rather than a dependency guard: each mint is a new
  // invitation, and a second one on a re-render would replace the one on screen.
  const { mutate } = mint;
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    mutate();
  }, [mutate]);

  const link = grant === null ? null : linkFor(grant.access_token);

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={heading}
        className="flex flex-col gap-16"
        testId="roster-invitation-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>

        {/* Creating until the mint answers, one way or the other. Read off the dialog's own outcome
            rather than `mint.isPending`: a mutation fired from a mount effect can lose its observer
            to React's development re-subscribe and stay "pending" beside its own answer. */}
        {grant === null && error === null && !alreadyJoined ? (
          <p
            className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
            {...testIdProp('roster-invitation-pending')}
          >
            Creating the invitation…
          </p>
        ) : null}

        {alreadyJoined ? (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp('roster-invitation-already-joined')}
          >
            {seat.display_name} has already joined with their Givmo account.
          </p>
        ) : null}

        {error !== null ? (
          <ErrorMessage testId="roster-invitation-error">{error}</ErrorMessage>
        ) : null}

        {grant !== null && link !== null ? (
          <InvitationOutcome seat={seat} grant={grant} link={link} />
        ) : null}

        <InvitationActions link={link} onClose={onClose} />
      </Dialog.Content>
    </Dialog.Root>
  );
}
